import { ulid } from 'ulid';
import { Base64 } from 'js-base64';
import moment from 'moment';
import tippy from 'tippy.js';
import sanitizeHtml from 'sanitize-html';

import './pagination.css';

/**
 * @type Class
 */
const Pagination = (function () {
	"use strict";

	// ============ UTILIDADES ============

	var error = message => {
		throw new Error(message);
	}

	var base64 = value => {
		return Base64.encode(value);
	}

	var purifyHTML = value => {
		return sanitizeHtml(value);
	}

	var typeWatchTimer = 0;

	var typeWatch = function () {
		return function (callback, ms) {
			clearTimeout(typeWatchTimer);
			typeWatchTimer = setTimeout(callback, ms);
		}
	}();

	var cancelTypeWatch = function () {
		clearTimeout(typeWatchTimer);
	};

	//EN REALIDAD CUALQUIER STRING ES HTML PERO BUENO
	var isHTML = (str) => {
		const a = document.createElement('div');
		a.innerHTML = str;

		for (let c = a.childNodes, i = c.length; i--;) {
			if (c[i].nodeType == 1) return true;
		}

		return false;
	}

	var normalizarString = string => string.toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

	var ajax = async function (url, method = "GET", data = {}, headers = {}) {
		let payload = {
			method: method,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json;charset=UTF-8',
				"X-Requested-With": "XMLHttpRequest"
			}
		};

		payload["headers"] = deepMerge(payload["headers"], headers);

		if (method != "GET") {
			payload["body"] = JSON.stringify(data);
		} else {
			let params = objectToQuerystring(data);
			url = url.includes('?') ? `${url}&${params}` : `${url}?${params}`;
		}

		try {
			let response = await fetch(url, payload);

			if (response.status >= 400) {
				error("Error devuelto por el servidor");
			}

			return await response.json();
		} catch (e) {
			error(e);

			return null;
		}
	}

	var objectToQuerystring = function (obj, prefix) {
		let str = [];
		let p;

		for (p in obj) {
			if (obj.hasOwnProperty(p) && obj[p] != undefined) {
				let k = prefix ? prefix + "[" + p + "]" : p;
				let v = obj[p];
				str.push((v !== null && typeof v === "object") ? objectToQuerystring(v, k) : encodeURIComponent(k) + "=" + encodeURIComponent(v));
			}
		}

		return str.join("&");
	}

	var deepMerge = function (target, source) {
		for (const key of Object.keys(source)) {
			if (source[key] instanceof Object) {
				target[key] = (target[key] instanceof Object) ? target[key] : {};
				Object.assign(source[key], deepMerge(target[key], source[key]))
			}
		}
		Object.assign(target || {}, source);
		return target;
	}

	var escapeHTML = function (unsafe) {
		return unsafe
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#039;");
	}

	// ============ PAGINATION ============

	var Pagination = function (element, config) {

		const METHODS = {};

		const CONFIG = {
			data: [],
			method: "GET",
			headers: {},
			body: {},
			filters: {},
			orders: {},
			search: false,
			searchKeys: null,
			table: false,
			footer: true,
			pagination: {
				initPage: 0,
				backToTop: true,
				pageLength: 50,
			},
			style: {
				theme: "",
				tagWrapper: null,
				tagWrapperClass: "",
				headerClass: "",
				bodyClass: "",
				contentClass: "",
				footerClass: "",
			},
			backend: {
				supportsMultipleFilters: false,
			},
			draw: () => { },
			onAjax: () => { },
			onError: () => { },
			onPageDrawn: () => { },
			onFilterBoxOpened: () => { },
			onFilterBoxClosed: () => { },
			onOrderBoxOpened: () => { },
			onOrderBoxClosed: () => { },
			onSearchBoxOpened: () => { },
			onSearchBoxClosed: () => { },
		};

		const STATE = {
			object: null,
			dom: {
				actionsWrapper: null,
				tagWrapper: null,
				preHeader: null,
				header: null,
				table: null,
				body: null,
				footer: null,
			},
			pagination: {
				data: [],
				currentPage: 0,
				totalLength: 0,
				filteredLength: 0,
				filters: {},
				orders: {}
			},
			filters: {},
			orders: {},
			actions: {},
			filterAction: null,
			orderAction: null,
		};

		const BLOCKING_AJAX = [];

		const BLOCKING = {
			activeReloadID: null,
		};

		const RESTRICTED_CLASSES = [
			"pagination",
			"pagination-header",
			"pagination-body",
			"pagination-footer",
		];

		// ============ "PUBLIC" METHODS ============

		METHODS.getState = function () {
			return STATE;
		}

		METHODS.getConfig = function () {
			return CONFIG;
		}

		METHODS.reload = async function (page = null) {
			const reloadID = ulid();
			BLOCKING.activeReloadID = reloadID;

			setTimeout(async () => {
				if (BLOCKING.activeReloadID != reloadID) return;

				page = page == null ? STATE.pagination.currentPage : page;
				await METHODS.selectPage(page);
				METHODS.updateSelectAutocomplete();
			}, 100)
		}

		METHODS.updateSelectAutocomplete = function () {
			Object.keys(STATE.filters).forEach(key => {
				const filter = STATE.filters[key];

				if (filter.type == "select" && filter.downloadAutocompleteOnReload) {
					METHODS.inputFilterAutocomplete(filter);
				}
			})
		}

		METHODS.selectPage = async function (page) {
			Promise.all(BLOCKING_AJAX).then(() => {
				BLOCKING_AJAX[0] = new Promise(async (resolve, reject) => {
					STATE.pagination.currentPage = page;

					if (CONFIG.pagination.backToTop) {
						if(CONFIG.table){
							const rows = STATE.dom.body.querySelectorAll('tbody tr');
							if(rows && rows.length > 0){
								rows[0].scrollIntoView({
									behavior: 'instant',
									block: 'center'
								});
							}
						}else{
							STATE.dom.body.scrollTop = 0;
						}
					}

					await METHODS.buildContent();
					METHODS.drawPage();
					resolve(true)
				})
			})
		}

		METHODS.filters = function () {
			return METHODS.getAjaxFilters();
		}

		METHODS.orders = function () {
			return METHODS.getAjaxOrders();
		}

		METHODS.addFilter = function (options) {
			METHODS.verifyAddFilterParameters(options);

			let filterID = ulid();

			STATE.filters[filterID] = METHODS.buildFilterObject(filterID, element, options);
			STATE.filters[filterID]["dom"] = METHODS.getFilterDOM(STATE.filters[filterID]);

			METHODS.addFilterToActions(STATE.filters[filterID]);
			METHODS.executeFilterActions(STATE.filters[filterID]);
		}

		METHODS.addCustomFilter = function (element, options) {
			METHODS.verifyAddCustomFilterParameters(element, options);

			let filterID = ulid();
			element.setAttribute("id-filter", filterID);
			element.classList.add("pagination-custom-filter")

			METHODS.addCustomFilterListener(element);

			STATE.filters[filterID] = METHODS.buildFilterObject(filterID, element, options);

			if (STATE.filters[filterID]["dom"].value != "") {
				METHODS.setFilter(STATE.filters[filterID]["field"], STATE.filters[filterID]["dom"].value, STATE.filters[filterID]["tag"], STATE.filters[filterID]["alias"])
			}
		}

		METHODS.buildFilterObject = (filterID, element, options) => {
			return {
				id: filterID,
				dom: element,
				format: options.format,
				type: options.type ? options.type : "text",
				field: options.field,
				tag: options.tag != undefined ? options.tag : true,
				alias: options.alias || options.field,
				autocomplete: options.autocomplete,
				downloadAutocompleteOnReload: options.downloadAutocompleteOnReload || false,
				autocompleteTimeout: 500,
				_filterSet: options._filterSet ? options._filterSet : () => { },
				_filterUnset: options._filterUnset ? options._filterUnset : () => { },
			};
		}

		METHODS.verifyAddCustomFilterParameters = function (element, opt) {
			if (!element || !(element instanceof Element)) {
				error("El primer parámetro de addCustomFilter no es un elemento válido");
			}

			if (!(element instanceof HTMLInputElement) && !(element instanceof HTMLSelectElement)) {
				error("El primer parámetro de addCustomFilter debe ser un input o un select");
			}

			if (!opt.field) {
				error("El segundo parámetro de addCustomFilter debe tener un campo 'field'");
			}
		}

		METHODS.setFilter = function (field, value, tag = true, fieldAlias = null, valueAlias = null, reload = true) {
			const existingFilter = Object.values(STATE.filters).find(filter => filter["field"] == field);
			if (existingFilter && !fieldAlias) {
				fieldAlias = existingFilter["alias"];
			}

			if (!STATE.pagination.filters[field]) {
				STATE.pagination.filters[field] = [];
			}

			//SOPORTE PARA MULTI VALORES POR FILTRO
			if (!CONFIG.backend.supportsMultipleFilters) {
				STATE.pagination.filters[field].forEach(v => {
					METHODS.deleteFilterTag(field, v);
				})
				STATE.pagination.filters[field] = []
			}

			STATE.pagination.filters[field].push(value);

			if (tag) {
				METHODS.createFilterTag(field, value, fieldAlias, valueAlias);
			}

			const filter = Object.values(STATE.filters).find(filter => filter["field"] == field);
			if (filter) {
				if (typeof filter._filterSet === 'function') {
					filter._filterSet(field, value, tag, fieldAlias, valueAlias, reload);
				}
			}

			if (reload) {
				METHODS.reload(0);
			}
		}

		METHODS.unsetFilter = function (field, value = null) {
			if (!Array.isArray(STATE.pagination.filters[field])) {
				STATE.pagination.filters[field] = [];
			}

			if (value != null) {
				while (true) {
					let index = STATE.pagination.filters[field].findIndex(e => e == value);
					if (index == -1) break;
					STATE.pagination.filters[field].splice(index, 1);
				}

				METHODS.deleteFilterTag(field, value);
			} else {
				STATE.pagination.filters[field].forEach(v => {
					METHODS.deleteFilterTag(field, v);
				})
				STATE.pagination.filters[field] = []
			}

			const filter = Object.values(STATE.filters).find(filter => filter["field"] == field);
			if (filter) {
				filter._filterUnset(field, value);
			}

			METHODS.reload(0);
		}

		METHODS.addOrder = function (options) {
			METHODS.verifyAddOrderParameters(options);

			let orderID = ulid();

			STATE.orders[orderID] = {
				id: orderID,
				field: options.field,
				tag: options.tag != undefined ? options.tag : true,
				alias: options.alias || options.field
			}

			METHODS.addOrderToActions(STATE.orders[orderID]);
		}

		METHODS.setOrder = async function (field, value, tag = true, fieldAlias = null, valueAlias = null, reload = true) {
			if (STATE.pagination.orders[field]) {
				METHODS.unsetOrder(field);
			}

			STATE.pagination.orders[field] = {
				field: field,
				value: value,
				timestamp: new Date().getTime(),
			}

			if (tag) {
				METHODS.createOrderTag(field, value, fieldAlias, valueAlias);
			}

			if (reload) {
				METHODS.reload(0);
			}
		}

		METHODS.unsetOrder = function (field, reaload = true) {
			STATE.pagination.orders[field] = null;

			METHODS.deleteOrderTag(field);

			if (reaload) {
				METHODS.reload(0);
			}
		}

		METHODS.cleanOrders = function () {
			Object.keys(STATE.orders).forEach(key => {
				const order = STATE.orders[key];

				if (order.value !== null) {
					METHODS.unsetOrder(order.field, false);
				}
			})

			METHODS.reload(0);
		}

		METHODS.updateData = function (data) {
			CONFIG.data = data;
			METHODS.reload();
		}

		METHODS.updateConfig = function (config) {
			deepMerge(CONFIG, config);
			METHODS.reload();
		}

		METHODS.addSearchAction = function () {
			let searchActionObject = METHODS.getSearchActionObject()
			STATE.dom.actionsWrapper.appendChild(searchActionObject)
		}

		METHODS.addAction = function (options) {
			METHODS.verifyAddActionParameters(options);

			let actionID = ulid();

			STATE.actions[actionID] = {
				object: null,
				title: options.title,
				icon: options.icon,
				onClick: options.onClick
			}

			STATE.actions[actionID]["object"] = METHODS.getActionObject(STATE.actions[actionID])
			STATE.dom.actionsWrapper.appendChild(STATE.actions[actionID]["object"])

			return actionID;
		}

		METHODS.removeAction = function (actionID) {
			if (!STATE.actions[actionID]) error("No existe una acción con ese ID");

			STATE.actions[actionID]["object"].remove()
			STATE.actions[actionID] = null
		}

		// ============ METHODS ============

		METHODS.searchOrderByID = function (id) {
			let found = null;

			Object.keys(STATE.orders).forEach(key => {
				const order = STATE.orders[key];

				if (order.id == id) {
					found = order;
				}
			})

			return found;
		}

		METHODS.searchOrderByField = function (field) {
			let found = null;

			Object.keys(STATE.orders).forEach(key => {
				const order = STATE.orders[key];

				if (order.field == field) {
					found = order;
				}
			})

			return found;
		}

		METHODS.addOrderToActions = order => {
			if (!STATE.orderAction) {
				METHODS.addOrderAction();
			}

			const orderWrapperSelect = STATE.orderAction.querySelector(".order-wrapper ul");
			if (!orderWrapperSelect) return;

			const li = document.createElement("li");
			li.innerText = order.alias;

			li.setAttribute("alias", order.alias)
			li.setAttribute("field", order.field)
			li.addEventListener("click", METHODS.orderResultClickListener)

			orderWrapperSelect.appendChild(li);
		}

		METHODS.addOrderAction = function () {
			let orderActionObject = METHODS.getOrderActionObject()
			STATE.dom.actionsWrapper.appendChild(orderActionObject)
			STATE.orderAction = orderActionObject;
		}

		METHODS.getOrderActionObject = function () {
			let button = document.createElement("button");

			button.classList.add("pagination-order-action")
			button.setAttribute("aria-label", "Ordenar resultados");

			button.addEventListener("click", e => {
				e.stopPropagation()
				METHODS.orderActionToggle();
			});

			button.innerHTML = `<span></span>
                                <div class="order-wrapper">
                                    <div class="order-wrapper-header">
                                        <p>Ordenar por</p>
                                        <span class="order-clean"></span>
                                        <div class="order-wrapper-tags"></div>
                                    </div>
                                    <div class="order-wrapper-body">
                                        <div class="order-container">
                                            <input placeholder="Seleccione una opción..." readonly></input>
                                            <ul class="order-results"></ul>
                                        </div>
                                        <button class="order-type asc active"></button>
                                        <button class="order-type desc"></button>
                                    </div>
                                    <button class="order-confirm">Ordenar</button>
                                </div>`;

			const orderWrapper = button.querySelector(".order-wrapper");
			orderWrapper.addEventListener("click", event => {
				event.stopPropagation()
				METHODS.closeOpenOrderResults();
			})

			const orderClean = button.querySelector(".order-clean");
			orderClean.addEventListener("click", METHODS.cleanOrders)

			const orderConfirm = button.querySelector(".order-confirm");
			orderConfirm.addEventListener("click", METHODS.orderConfirmClickListener)

			button.querySelectorAll(".order-type").forEach(type => {
				type.addEventListener("click", METHODS.orderTypeConfirmClickListener)
			});

			const input = button.querySelector("input");
			input.addEventListener("click", event => {
				event.stopPropagation()
				METHODS.toggleOrderResults()
			})

			return button;
		}

		METHODS.addFilterToActions = filter => {
			if (!STATE.filterAction) {
				METHODS.addFilterAction();
			}

			const filterWrapper = STATE.filterAction.querySelector(".filter-wrapper");
			if (!filterWrapper) return;

			const div = document.createElement("div");
			div.classList.add("filter-container");

			div.appendChild(filter.dom);

			filterWrapper.appendChild(div);
		}

		METHODS.addFilterAction = function () {
			let filterActionObject = METHODS.getFilterActionObject()
			STATE.dom.actionsWrapper.appendChild(filterActionObject)
			STATE.filterAction = filterActionObject;
		}

		METHODS.getFilterActionObject = function () {
			let button = document.createElement("button");
			let icon = document.createElement("span");
			let div = document.createElement("div");

			div.classList.add("filter-wrapper");
			div.addEventListener("click", event => {
				event.stopPropagation()

				if (event.detail !== 0) {
					METHODS.closeOpenFilterResults();
				}
			});
			div.addEventListener("scroll", METHODS.closeOpenFilterResults);

			button.addEventListener("click", event => {
				event.stopPropagation();

				METHODS.filterActionToggle();
			});

			button.appendChild(icon);
			button.appendChild(div);

			button.classList.add("pagination-filter-action");
			button.setAttribute("aria-label", "Filtrar resultados");

			return button;
		}

		METHODS.getSearchActionObject = function () {
			let button = document.createElement("button");
			let icon = document.createElement("span");
			let input = document.createElement("input");

			input.setAttribute("type", "text");
			input.setAttribute("autocomplete", "off");
			input.setAttribute("placeholder", "Buscar...");
			input.addEventListener("input", METHODS.searchActionInputListener)
			input.addEventListener("click", event => event.stopPropagation())

			button.addEventListener("click", event => {
				event.stopPropagation()

				if (event.detail !== 0) {
					METHODS.searchActionToggle();
				}
			});

			button.appendChild(icon);
			button.appendChild(input);

			button.classList.add("pagination-search-action");
			button.setAttribute("aria-label", "Buscar resultados");

			return button;
		}

		METHODS.searchActionIsOpen = function () {
			const button = STATE.object.querySelector(".pagination-search-action");
			if (!button) return false;

			return button.classList.contains("active") ? true : false;
		}

		METHODS.searchActionToggle = function () {
			const button = STATE.object.querySelector(".pagination-search-action");
			if (!button) return;

			METHODS.closeOpenFilterResults();
			METHODS.closeOpenOrderResults();

			if (button.classList.contains("pagination-search-action")) {
				button.classList.toggle("active")

				if (button.classList.contains("active")) {
					const input = button.querySelector("input");
					if (input) {
						input.focus();
						input.value = "";
					}
					CONFIG.onSearchBoxOpened();
				} else {
					CONFIG.onSearchBoxClosed();
				}
			}
		}

		METHODS.closeSearchAction = function () {
			if (METHODS.searchActionIsOpen()) {
				METHODS.searchActionToggle();
			}
		}

		METHODS.orderActionIsOpen = function () {
			const button = STATE.object.querySelector(".pagination-order-action");
			if (!button) return false;

			return button.classList.contains("active") ? true : false;
		}

		METHODS.orderActionToggle = function () {
			const button = STATE.object.querySelector(".pagination-order-action");
			if (!button) return;

			METHODS.closeOpenFilterResults();
			METHODS.closeOpenOrderResults();

			if (button.classList.contains("pagination-order-action")) {
				METHODS.closeFilterAction();
				button.classList.toggle("active");

				if (button.classList.contains("active")) {
					CONFIG.onOrderBoxOpened();
				} else {
					CONFIG.onOrderBoxClosed();
				}
			}
		}

		METHODS.closeOrderAction = function () {
			if (METHODS.orderActionIsOpen()) {
				METHODS.orderActionToggle();
			}
		}

		METHODS.filterActionIsOpen = function () {
			const button = STATE.object.querySelector(".pagination-filter-action");
			if (!button) return false;

			return button.classList.contains("active") ? true : false;
		}

		METHODS.filterActionToggle = function () {
			const button = STATE.object.querySelector(".pagination-filter-action");
			if (!button) return;

			METHODS.closeOpenFilterResults();
			METHODS.closeOpenOrderResults();

			if (button.classList.contains("pagination-filter-action")) {
				METHODS.closeOrderAction();
				button.classList.toggle("active");

				if (button.classList.contains("active")) {
					CONFIG.onFilterBoxOpened();
				} else {
					CONFIG.onFilterBoxClosed();
				}
			}
		}

		METHODS.closeFilterAction = function () {
			if (METHODS.filterActionIsOpen()) {
				METHODS.filterActionToggle();
			}
		}

		METHODS.getActionIconObject = function (icon) {
			const div = document.createElement("div");
			div.classList.add("icon-wrapper");

			//NO ES HTML ENTONCES ES FONT AWESOME
			if (!isHTML(icon)) {
				const iconDOM = document.createElement("i");
				iconDOM.className = icon;
				div.appendChild(iconDOM);
			} else {
				div.innerHTML = icon
			}

			return div;
		}

		METHODS.getActionObject = function (options) {
			const button = document.createElement("button");
			const icon = METHODS.getActionIconObject(options.icon);
			const p = document.createElement("p");

			p.innerText = options.title
			button.onclick = options.onClick

			button.appendChild(icon);
			button.appendChild(p);

			METHODS.addTooltipToActionObject(button, options.title);

			return button;
		}

		METHODS.addTooltipToActionObject = function (button, text) {
			tippy(button, {
				content: text.toUpperCase(),
				onShow: () => {
					return window.innerWidth <= 1000
				},
				zIndex: 900000,
			});
		}

		METHODS.verifyAddActionParameters = function (options) {
			if (!options.title) {
				error("Debe pasar una propiedad 'title' en el primer parámetro de 'addAction'");
			}
			if (typeof options.title !== 'string') {
				error("La propiedad 'title' debe ser un string");
			}
			if (!options.icon) {
				error("Debe pasar una propiedad 'icon' en el primer parámetro de 'addAction'");
			}
			if (typeof options.icon !== 'string') {
				error("La propiedad 'icon' debe ser un string");
			}
			if (!options.onClick) {
				error("Debe pasar una propiedad 'onClick' en el primer parámetro de 'addAction'");
			}
			if (typeof options.onClick !== 'function') {
				error("La propiedad 'onClick' debe ser una función");
			}
		}

		METHODS.deleteFilterTag = function (field, value) {
			STATE.dom.tagWrapper.querySelectorAll(`.pagination-filter[field='${base64(field)}'][value='${base64(value)}']`).forEach(e => {
				e.remove()
			});
		}

		METHODS.deleteOrderTag = function (field) {
			if (!STATE.orderAction) {
				return;
			}

			STATE.orderAction.querySelectorAll(`button[field='${base64(field)}']`).forEach(e => {
				e.remove()
			});
		}

		METHODS.createOrderTag = function (field, value, fieldAlias = null, valueAlias = null) {
			if (!STATE.orderAction) {
				METHODS.addOrderAction();
			}

			const tagWrapper = STATE.orderAction.querySelector(".order-wrapper-tags");

			let button = document.createElement("button");

			button.setAttribute("field", base64(field));

			button.innerText = `${fieldAlias || field}: ${valueAlias || value}`;

			button.addEventListener("click", e => {
				METHODS.unsetOrder(field);
			})

			tagWrapper.appendChild(button);
		}

		METHODS.createFilterTag = function (field, value, fieldAlias = null, valueAlias = null) {
			let div = document.createElement("div");
			div.classList.add("pagination-filter");

			div.setAttribute("field", base64(field));
			div.setAttribute("value", base64(value));

			div.innerText = `${fieldAlias || field}: ${valueAlias || value}`;

			div.addEventListener("click", e => {
				METHODS.unsetFilter(field, value);
			})

			STATE.dom.tagWrapper.appendChild(div);
		}

		METHODS.executeFilterActions = function (filter) {
			if (filter.type === "select") {
				METHODS.inputFilterAutocomplete(filter);
			}
		}

		METHODS.getFilterDOM = function (filter) {
			const wrapper = document.createElement("div");
			wrapper.classList.add("filter");

			let el = null;

			if (filter.type === "text") {
				el = METHODS.createTextFilter(filter)
			}

			if (filter.type === "number") {
				el = METHODS.createNumberFilter(filter)
			}

			if (filter.type === "select") {
				el = METHODS.createSelectFilter(filter)
				wrapper.classList.add("filter-select");
			}

			if (filter.type === "date") {
				el = METHODS.createDateFilter(filter)
			}

			if (filter.type === "datetime") {
				el = METHODS.createDateTimeFilter(filter)
			}

			if (!el) error("El tipo de filtro no es válido");

			const p = document.createElement("p");
			p.innerHTML = `${filter.alias.substring(0, 1)}&zwnj;${filter.alias.substring(1, filter.alias.length)}`;

			wrapper.appendChild(p);
			wrapper.appendChild(el);

			return wrapper;
		}

		METHODS.createTextFilter = function (filter) {
			let input = document.createElement("input");
			input.setAttribute("type", "text");
			input.setAttribute("id-filter", filter["id"]);
			input.setAttribute("autocomplete", "off");
			input.addEventListener("input", METHODS.inputFilterInputListener)
			input.addEventListener("keypress", METHODS.inputFilterKeyPressListener)

			input.addEventListener("click", event => {
				if (filter.autocomplete) {
					event.stopPropagation();
				}
			});

			return input;
		}

		METHODS.createNumberFilter = function (filter) {
			let input = document.createElement("input");
			input.setAttribute("type", "number");
			input.setAttribute("id-filter", filter["id"]);
			input.setAttribute("autocomplete", "off");
			input.addEventListener("input", METHODS.inputFilterInputListener)
			input.addEventListener("keypress", METHODS.inputFilterKeyPressListener)

			input.addEventListener("click", event => {
				if (filter.autocomplete) {
					event.stopPropagation();
				}
			});

			return input;
		}

		METHODS.createSelectFilter = function (filter) {
			let input = document.createElement("input");
			input.setAttribute("placeholder", "Seleccione una opción");
			input.setAttribute("readonly", "true");
			input.setAttribute("id-filter", filter["id"]);
			input.addEventListener("click", e => {
				e.stopPropagation();
				METHODS.toggleFilterResults(input);
			});
			return input;
		}

		METHODS.createDateFilter = function (filter) {
			let input = document.createElement("input");
			input.setAttribute("type", "date");
			input.setAttribute("id-filter", filter["id"]);
			input.setAttribute("autocomplete", "off");
			input.addEventListener("change", METHODS.datetimeFilterChangeListener)
			return input;
		}

		METHODS.createDateTimeFilter = function (filter) {
			let input = document.createElement("input");
			input.setAttribute("type", "datetime");
			input.setAttribute("id-filter", filter["id"]);
			input.setAttribute("autocomplete", "off");
			input.addEventListener("change", METHODS.datetimeFilterChangeListener)
			return input;
		}

		METHODS.closeOpenActions = () => {
			METHODS.closeFilterAction();
			METHODS.closeOrderAction();
			METHODS.closeSearchAction();
		}

		METHODS.openOrderResults = () => {
			if (!STATE.orderAction) return;

			const orderResults = STATE.orderAction.querySelector(".order-results");
			if (!orderResults) return;

			if (!orderResults.classList.contains("active")) {
				METHODS.closeOpenOrderResults();

				orderResults.classList.add("active");
				METHODS.updateOrderResultsPosition();
			}
		}

		METHODS.closeOpenOrderResults = () => {
			if (!STATE.orderAction) return;

			const orderResults = STATE.orderAction.querySelector(".order-results");
			if (!orderResults) return;

			if (orderResults.classList.contains("active")) {
				orderResults.classList.remove("active");
			}
		}

		METHODS.toggleOrderResults = () => {
			if (!STATE.orderAction) return;

			const orderResults = STATE.orderAction.querySelector(".order-results");
			if (!orderResults) return;

			if (orderResults.classList.contains("active")) {
				METHODS.closeOpenOrderResults();
			} else {
				METHODS.openOrderResults();
			}
		}

		METHODS.updateOrderResultsPosition = () => {
			const ul = STATE.orderAction.querySelector(".order-results");
			if (!ul) return;

			const input = STATE.orderAction.querySelector("input");
			if (!input) return;

			const viewportFilter = input.getBoundingClientRect();
			const viewportUl = ul.getBoundingClientRect();

			const defaultTop = viewportFilter.top + viewportFilter.height;
			const secondTop = window.innerHeight - viewportFilter.bottom + viewportFilter.height + 10;

			ul.style.left = `${viewportFilter.left}px`;
			ul.style.width = `${viewportFilter.width}px`;

			if (defaultTop + viewportUl.height < window.innerHeight) {
				ul.style.top = `${defaultTop}px`;
				ul.style.bottom = `inherit`;
				ul.classList.add("top");
				ul.classList.remove("bottom");
			} else {
				ul.style.top = `inherit`;
				ul.style.bottom = `${secondTop}px`;
				ul.classList.remove("top");
				ul.classList.add("bottom");
			}
		}

		METHODS.openFilterResults = input => {
			const filterDOM = input.closest(".filter");
			if (!filterDOM) return;

			if (!filterDOM.classList.contains("active")) {
				METHODS.closeOpenFilterResults();

				filterDOM.classList.add("active");
				METHODS.updateFilterResultsPosition(filterDOM);
			}
		}

		METHODS.closeOpenFilterResults = () => {
			if (!STATE.filterAction) return;

			STATE.filterAction.querySelectorAll(".filter.active").forEach(e => {
				e.classList.remove("active");
			})
		}

		METHODS.toggleFilterResults = input => {
			const filterDOM = input.closest(".filter");
			if (!filterDOM) return;

			if (filterDOM.classList.contains("active")) {
				METHODS.closeOpenFilterResults();
			} else {
				METHODS.openFilterResults(input);
			}
		}

		METHODS.updateFilterResultsPosition = filterDOM => {
			const ul = filterDOM.querySelector("ul");
			if (!ul) return;

			const viewportFilter = filterDOM.getBoundingClientRect();
			const viewportUl = ul.getBoundingClientRect();

			const defaultTop = viewportFilter.top + viewportFilter.height;
			const secondTop = window.innerHeight - viewportFilter.bottom + viewportFilter.height + 10;

			ul.style.left = `${viewportFilter.left}px`;
			ul.style.width = `${viewportFilter.width}px`;

			if (defaultTop + viewportUl.height < window.innerHeight) {
				ul.style.top = `${defaultTop}px`;
				ul.style.bottom = `inherit`;
				ul.classList.add("top");
				ul.classList.remove("bottom");
			} else {
				ul.style.top = `inherit`;
				ul.style.bottom = `${secondTop}px`;
				ul.classList.remove("top");
				ul.classList.add("bottom");
			}
		}

		METHODS.verifyAddOrderParameters = function (options) {
			if (!options.field) {
				error("Debe pasar una propiedad 'field'en las opciones");
			}
		}

		METHODS.verifyAddFilterParameters = function (options) {
			if (!options.field) {
				error("Debe pasar una propiedad 'field' en las opciones");
			}

			const filter = Object.values(STATE.filters).find(filter => filter["field"] == options.field);
			if (filter) {
				error("Ya existe un filtro con ese campo field");
			}
		}

		METHODS.buildHeadersDOM = () => {
			if (CONFIG.table) {
				const tr = document.createElement("tr");

				STATE.dom.preHeader.forEach(e => {
					const th = document.createElement("th");

					th.appendChild(e);
					tr.appendChild(th);
				})

				STATE.dom.header.appendChild(tr)
			} else {
				STATE.dom.preHeader.forEach(e => {
					STATE.dom.header.appendChild(e)
				})
			}
		}

		METHODS.obtainPaginationHTML = () => {
			if (CONFIG.table) {
				return `<table class="table-scroll">
							<thead class="pagination-header ${CONFIG.style.headerClass}"></thead>
							<tbody class="pagination-body ${CONFIG.style.bodyClass}"></tbody>
						</table>`;
			} else {
				return `<div class="pagination-header ${CONFIG.style.headerClass}"></div>
						<div class="pagination-body ${CONFIG.style.bodyClass}">
							<div class="pagination-content ${CONFIG.style.contentClass}"></div>
						</div>`;
			}
		}

		METHODS.createDOM = function (callback = null) {
			METHODS.createDOMCheckExceptions();

			STATE.object.classList.add(`pagination`);

			if (CONFIG.style.theme) {
				STATE.object.classList.add(CONFIG.style.theme);
			}

			if (!CONFIG.footer) {
				STATE.object.classList.add("no-footer");
			}

			if (CONFIG.table) {
				STATE.object.classList.add("table-mode");
			}

			STATE.object.innerHTML = `<div class="pagination-tools-wrapper">
                                            <div class="pagination-actions-wrapper"></div>
                                            <div class="pagination-filter-wrapper ${CONFIG.style.tagWrapperClass}"></div>
                                        </div>
										<div class="pagination-wrapper">
											${METHODS.obtainPaginationHTML()}
											<div class="pagination-loading"></div>
										</div>
                                        <div class="pagination-footer ${CONFIG.style.footerClass}"></div>`;

			STATE.dom.actionsWrapper = STATE.object.querySelector(`.pagination-actions-wrapper`);
			STATE.dom.tagWrapper = CONFIG.style.tagWrapper ? CONFIG.style.tagWrapper : STATE.object.querySelector(`.pagination-filter-wrapper`);
			STATE.dom.header = STATE.object.querySelector(`.pagination-header`);
			STATE.dom.table = STATE.object.querySelector(`table`);
			STATE.dom.body = STATE.object.querySelector(`.pagination-body`);
			STATE.dom.footer = STATE.object.querySelector(`.pagination-footer`);

			METHODS.buildHeadersDOM();

			return callback ? callback() : true;
		}

		METHODS.createDOMCheckExceptions = function () {
			if (RESTRICTED_CLASSES.includes(CONFIG.style.headerClass)) {
				error("La clase de headerClass no puede ser igual a las que vienen por defecto.")
			}
			if (RESTRICTED_CLASSES.includes(CONFIG.style.bodyClass)) {
				error("La clase de bodyClass no puede ser igual a las que vienen por defecto.")
			}
			if (RESTRICTED_CLASSES.includes(CONFIG.style.footerClass)) {
				error("La clase de footerClass no puede ser igual a las que vienen por defecto.")
			}
		}

		METHODS.buildContent = async function () {
			if (Array.isArray(CONFIG.data)) {
				METHODS.buildArrayContent();
			} else {
				await METHODS.buildAjaxContent();
			}
		}

		METHODS.buildArrayContent = function () {
			let data = METHODS.filterArrayContent(CONFIG.data);
			data = METHODS.sortArrayContent(data);

			let start = CONFIG.pagination.pageLength != null ? STATE.pagination.currentPage * CONFIG.pagination.pageLength : 0;
			let length = CONFIG.pagination.pageLength != null ? start + CONFIG.pagination.pageLength : data.length;

			STATE.pagination.data = data.slice(start, length);
			STATE.pagination.totalLength = CONFIG.data.length;
			STATE.pagination.filteredLength = data.length;
		}

		METHODS.filterArrayContent = function (data) {
			return data.slice().filter(item => {
				let keys = Object.keys(STATE.pagination.filters)
				if (keys.length == 0) return true;

				let oks = 0;
				let empty = 0

				const searchKeysFiltered = Array.isArray(CONFIG.searchKeys);
				
				keys.forEach(key => {
					if (Array.isArray(STATE.pagination.filters[key])) {
						if (STATE.pagination.filters[key].length == 0) {
							empty++;
						}

						let fil = false;

						STATE.pagination.filters[key].forEach(filterData => {
							if (fil) return;

							const fields = key == "buscador" ? Object.keys(item).filter(k => searchKeysFiltered ? CONFIG.searchKeys.includes(k) : true) : new Array(key);
							
							fields.forEach(field => {
								if (fil) return;

								if (normalizarString(String(item[field])).includes(normalizarString(String(filterData)))) {
									oks++;
									fil = true;
								}
							})
						})
					} else {
						empty++
					}
				})

				if (keys.length == empty) return true;

				return oks != keys.length - empty ? false : true;
			})
		}

		METHODS.compareValues = function (first, second, type) {
			if (first !== null && second !== null) {
				if (type == "ASC") {
					if (typeof first === 'string' && typeof second === 'string') {
						return first.localeCompare(second)
					} else {
						return first - second
					}
				}
				if (type == "DESC") {
					if (typeof first === 'string' && typeof second === 'string') {
						return second.localeCompare(first)
					} else {
						return second - first
					}
				}
				return 0
			} else {
				return 0;
			}
		}

		METHODS.sortArrayContent = function (data) {
			data = data.sort((a, b) => {
				let sorted = 0;

				METHODS.getOrderedOrdersWithValue().forEach(order => {
					if (sorted != 0) return;

					sorted = METHODS.compareValues(a[order.field], b[order.field], order.value);
				})

				return sorted;
			})

			return data;
		}

		METHODS.buildAjaxContent = async function () {
			let payload = {
				start: STATE.pagination.currentPage * CONFIG.pagination.pageLength,
				length: CONFIG.pagination.pageLength,
				filtros: METHODS.getAjaxFilters(),
				ordenes: METHODS.getAjaxOrders()
			};

			payload = deepMerge(payload, CONFIG.body);

			let response = null

			try {
				STATE.object.classList.add("loading");
				response = await ajax(`${CONFIG.data}`, CONFIG.method, payload, CONFIG.headers)
				STATE.object.classList.remove("loading");
			} catch (error) {
				STATE.object.classList.remove("loading");
				return CONFIG.onError(error)
			}

			METHODS.verifyAjaxResponseErrors(response);

			CONFIG.onAjax(response);

			STATE.pagination.data = response.data;
			STATE.pagination.totalLength = response.recordsTotal;
			STATE.pagination.filteredLength = response.recordsFiltered;
		}

		METHODS.verifyAjaxResponseErrors = function (response) {
			if (!response) {
				error("La respuesta esta vacia!");
			}
			if (!response.data || !Array.isArray(response.data)) {
				error("La respuesta debe contener un campo 'data' que almacene un Array");
			}
			if (!response.recordsTotal && response.recordsTotal != 0) {
				error("La respuesta debe contener un campo 'recordsTotal' que almacene el número total de registros");
			}
			if (!response.recordsFiltered && response.recordsFiltered != 0) {
				error("La respuesta debe contener un campo 'recordsFiltered' que almacene el número total de registros filtrados");
			}
		}

		METHODS.getOrderedOrdersWithValue = () => {
			const orders = [];

			Object.keys(STATE.pagination.orders).forEach(key => {
				const order = STATE.pagination.orders[key];
				if (!order || !order.value) return;

				orders.push(order)
			})

			orders.sort((a, b) => {
				return a.timestamp - b.timestamp;
			})

			return orders;
		}

		METHODS.getAjaxOrders = function () {
			const orders = []

			METHODS.getOrderedOrdersWithValue().forEach(order => {
				const data = []
				data[order.field] = order.value

				orders.push(data)
			})

			return orders;
		}

		METHODS.getAjaxFilters = function () {
			//SOPORTE PARA MULTI VALORES POR FILTRO
			if (CONFIG.backend.supportsMultipleFilters) {
				return STATE.pagination.filters;
			} else {
				let filters = {}
				Object.keys(STATE.pagination.filters).forEach(key => {
					if (Array.isArray(STATE.pagination.filters[key])) {
						if (STATE.pagination.filters[key][STATE.pagination.filters[key].length - 1] !== undefined) {
							filters[key] = STATE.pagination.filters[key][STATE.pagination.filters[key].length - 1]
						}
					}
				})
				return filters;
			}
		}

		METHODS.drawPage = () => {
			METHODS.drawBody();
			METHODS.drawFooter();
			CONFIG.onPageDrawn(STATE.pagination.data);
		}

		METHODS.drawCardBody = () => {
			const content = STATE.dom.body.querySelector(".pagination-content");
			if (!content) return;

			let html = ``;

			STATE.pagination.data.forEach((e, index) => {
				html += `${CONFIG.draw(e, index, STATE.pagination, null)}`
			});

			content.innerHTML = html;
		}

		METHODS.drawTableBody = () => {
			const div = document.createElement("div");

			STATE.dom.body.innerHTML = ``;

			STATE.pagination.data.forEach((e, index) => {
				const tr = document.createElement("tr");

				div.innerHTML = `${CONFIG.draw(e, index, STATE.pagination, tr)}`;

				Array.prototype.slice.call(div.children).forEach((child) => {
					const td = document.createElement("td");
					td.appendChild(child);
					tr.appendChild(td);
				})

				STATE.dom.body.appendChild(tr);
			});
		}

		METHODS.drawBody = () => {
			if (CONFIG.table) {
				METHODS.drawTableBody();
			} else {
				METHODS.drawCardBody();
			}

			if (STATE.pagination.data.length <= 0) {
				STATE.object.classList.add("no-content");
			} else {
				STATE.object.classList.remove("no-content");
			}
		}

		METHODS.drawFooter = () => {
			let lastPage = Math.ceil(STATE.pagination.filteredLength / CONFIG.pagination.pageLength);

			let footer = `<button class="pagination-prev ${(STATE.pagination.currentPage == 0) ? `dont-show` : ``}" page="${STATE.pagination.currentPage - 1}">
                            <p>ANTERIOR</p>
                        </button>`;
			if (STATE.pagination.currentPage - 2 > 0) {
				footer += `<button class="pagination-begin" page="${0}">${1}</button>`;
				footer += `<button disabled>...</button>`;
			}
			for (let i = 1; i >= 1; i--) {
				footer += (STATE.pagination.currentPage - i >= 0) ? `<button page="${STATE.pagination.currentPage - i}">${STATE.pagination.currentPage + 1 - i}</button>` : ``;
			}
			footer += `<button disabled class="selected">${STATE.pagination.currentPage + 1}</button>`;
			for (let i = 1; i <= 1; i++) {
				footer += (STATE.pagination.currentPage + i < lastPage) ? `<button page="${STATE.pagination.currentPage + i}">${STATE.pagination.currentPage + 1 + i}</button>` : ``;
			}
			if (STATE.pagination.currentPage + 2 < lastPage) {
				footer += `<button disabled>...</button>`;
				footer += `<button class="pagination-last" page="${lastPage - 1}">${lastPage}</button>`;
			}
			footer += `<button class="pagination-next ${(STATE.pagination.currentPage + 1 >= lastPage) ? `dont-show` : ``}" page="${STATE.pagination.currentPage + 1}">
                            <p>SIGUIENTE</p>
                        </button>`;

			const desde = STATE.pagination.data.length == 0 ? 0 : (STATE.pagination.currentPage * CONFIG.pagination.pageLength) + 1;
			const hasta = STATE.pagination.data.length == 0 ? 0 : desde + CONFIG.pagination.pageLength > CONFIG.pagination.pageLength ? (desde + STATE.pagination.data.length) - 1 : (desde + CONFIG.pagination.pageLength) - 1;
			const partialTotal = STATE.pagination.totalLength !== STATE.pagination.filteredLength ? STATE.pagination.filteredLength : STATE.pagination.totalLength;
			const absoluteTotal = STATE.pagination.totalLength;

			STATE.dom.footer.innerHTML = `<div class="footer-info">
                                            ${STATE.pagination.totalLength === 0
					? `<p>No hay registros</p>`
					: `<p>Mostrando del ${desde} al ${hasta} de ${partialTotal} registros ${partialTotal !== absoluteTotal ? `(${absoluteTotal} total)` : ``}</p>`
				}
                                        </div>
                                        <div class="footer-buttons">
                                            ${footer}
                                        </div>`;

			STATE.dom.footer.querySelectorAll("button").forEach(e => {
				METHODS.addFooterButtonListener(e);
			})
		}

		METHODS.addResultsToResultsWrapper = function (filterID, results, ul) {
			ul.innerHTML = ``;

			if (Array.isArray(results)) {
				results.forEach(e => {
					let li = document.createElement("li");
					li.innerText = e.label || e.value || "";
					li.setAttribute("title", e.label || e.value || "")
					li.setAttribute("alias", e.label || e.value || "")
					li.setAttribute("value", e.id || "")
					li.setAttribute("id-filter", filterID)
					li.addEventListener("click", METHODS.filterResultClickListener)
					ul.appendChild(li)
				})
			}

			if (Array.isArray(results) && results.length == 0) {
				let li = document.createElement("li");
				li.innerText = "No hay resultados";
				li.setAttribute("disabled", "true")
				ul.appendChild(li)
			}
		}

		METHODS.filterAutocomplete = async function (autocomplete, value = null) {
			let results = [];

			if (value === "") {
				return false;
			}

			if (Array.isArray(autocomplete)) {
				if (value !== null) {
					results = autocomplete.filter(e => normalizarString(e.id).includes(normalizarString(value)));
				} else {
					results = autocomplete;
				}
			} else {
				if (value !== null) {
					results = await ajax(autocomplete, "GET", {
						descripcion: value,
						filtros: METHODS.getAjaxFilters()
					})
				} else {
					results = await ajax(autocomplete, "GET", {
						filtros: METHODS.getAjaxFilters()
					})
				}
			}

			return Array.isArray(results) ? results : [];
		}

		METHODS.inputFilterAutocomplete = async function (filter) {
			if (!filter) {
				error("El filtro no existe");
			}

			const input = filter.dom.querySelector("input");
			const value = filter.type === "select" ? null : input.value;
			const results = await METHODS.filterAutocomplete(filter.autocomplete, value);
			const filterDOM = input.closest(".filter");

			let resultsWrapper = filterDOM.querySelector(".filter-results");
			if (!resultsWrapper) {
				resultsWrapper = document.createElement("ul");
				resultsWrapper.classList.add("filter-results");
				filterDOM.appendChild(resultsWrapper);
			}

			METHODS.addResultsToResultsWrapper(filter.id, results, resultsWrapper);

			if (filter.type !== "select") {
				METHODS.openFilterResults(input);
			}
		}

		METHODS.setInitFiltersOrders = () => {
			if (typeof CONFIG.filters !== 'object' || CONFIG.filters === null) error("'filters' en la configuración debe ser un objeto o un string");

			Object.keys(CONFIG.filters).forEach(key => {
				let filter = CONFIG.filters[key];

				if (typeof filter !== 'object' || filter === null) {
					filter = {
						value: filter
					};
				}

				if (!filter.value) error(`${key} debe tener una propiedad 'value' válida`);

				const tag = filter.tag === true || filter.tag === false ? filter.tag : true;

				METHODS.setFilter(key, filter.value, tag, filter.fieldAlias || key, filter.valueAlias || filter.value, false);
			})

			if (typeof CONFIG.orders !== 'object' || CONFIG.orders === null) error("'orders' en la configuración debe ser un objeto o un string");

			Object.keys(CONFIG.orders).forEach(key => {
				let order = CONFIG.orders[key];

				if (typeof order !== 'object' || order === null) {
					order = {
						value: order
					};
				}

				if (!order.value) error(`${key} debe tener una propiedad 'value' válida`);

				const tag = order.tag === true || order.tag === false ? order.tag : true;

				METHODS.setOrder(key, order.value, tag, order.fieldAlias || key, order.valueAlias || order.value, false);
			})
		}

		// ============ LISTENERS ============

		METHODS.addListeners = function () {
			METHODS.closeAllOpenFilterResultsListener();
			METHODS.closeAllOpenOrderResultsListener();
			METHODS.closeAllOpenActionsListener();
		}

		METHODS.closeAllOpenFilterResultsListener = function () {
			document.addEventListener("click", METHODS.closeOpenFilterResults);
			document.addEventListener("scroll", METHODS.closeOpenFilterResults);
			document.addEventListener("resize", METHODS.closeOpenFilterResults);
		}

		METHODS.closeAllOpenOrderResultsListener = function () {
			document.addEventListener("click", METHODS.closeOpenOrderResults);
			document.addEventListener("scroll", METHODS.closeOpenOrderResults);
			document.addEventListener("resize", METHODS.closeOpenOrderResults);
		}

		METHODS.closeAllOpenActionsListener = function () {
			document.addEventListener("click", METHODS.closeOpenActions);
			document.addEventListener("scroll", METHODS.closeOpenActions);
			document.addEventListener("resize", METHODS.closeOpenActions);
		}

		METHODS.addFooterButtonListener = function (element) {
			element.addEventListener("click", e => {
				let page = e.currentTarget.getAttribute("page");

				if (page != undefined) {
					METHODS.selectPage(parseInt(page, 10));
				}
			})
		}

		METHODS.inputFilterChangeListener = function (event) {
			let select = event.currentTarget;

			let filterID = select.getAttribute("id-filter")
			let filter = STATE.filters[filterID]

			if (!filter) {
				error("El filtro no existe");
			}

			METHODS.setFilter(filter.field, select.value, filter.tag, filter.alias);
		}

		METHODS.orderResultClickListener = function (event) {
			const li = event.currentTarget;
			const alias = li.getAttribute("alias");
			const field = li.getAttribute("field");

			if (!STATE.orderAction) return;

			const input = STATE.orderAction.querySelector("input");
			input.value = alias;
			input.setAttribute("field", field);
		}

		METHODS.filterResultClickListener = function (event) {
			const option = event.currentTarget;
			const value = option.getAttribute("value");
			const valueAlias = option.getAttribute("alias");

			let filterID = option.getAttribute("id-filter");
			let filter = STATE.filters[filterID];

			if (!filter) {
				error("El filtro no existe");
			}

			METHODS.setFilter(filter.field, value, filter.tag, filter.alias, valueAlias || null);
			METHODS.closeFilterAction();

			const filterDOM = option.closest(".filter");
			if (!filterDOM) return;

			const input = filterDOM.querySelector("input");
			if (!input) return;

			input.value = ``;
		}

		METHODS.datetimeFilterChangeListener = function (event) {
			let input = event.currentTarget;

			let filterID = input.getAttribute("id-filter")
			let filter = STATE.filters[filterID]

			if (!filter) {
				error("El filtro no existe");
			}

			let valor = input.value
			let date = null;

			try {
				date = new Date(valor);
			} catch (e) {
				console.error(e);
			}

			const year = date ? date.getFullYear() : null;

			if (filter.format) {
				valor = moment(valor).format(filter.format);
			}

			if (input.value != "" && year && year.toString().length === 4) {
				METHODS.setFilter(filter.field, valor, filter.tag, filter.alias);
				input.value = "";
			}
		}

		METHODS.inputFilterInputListener = function (event) {
			let input = event.currentTarget;

			let filterID = input.getAttribute("id-filter")
			let filter = STATE.filters[filterID]

			if (!filter) {
				error("El filtro no existe");
			}

			if (filter.autocomplete) {
				typeWatch(() => {
					METHODS.inputFilterAutocomplete(filter);
				}, filter.autocompleteTimeout);
			}
		}

		METHODS.searchActionInputListener = function (event) {
			const input = event.currentTarget;

			if (input.value == "") {
				METHODS.unsetFilter("buscador");
				cancelTypeWatch();
				return;
			};

			if (event.keyCode == 13) {
				METHODS.setFilter("buscador", input.value, true, "BÚSQUEDA");
				input.value = "";

				METHODS.searchActionToggle()
				cancelTypeWatch();
			} else {
				typeWatch(() => {
					METHODS.setFilter("buscador", input.value, true, "BÚSQUEDA");
				}, 500);
			}
		}

		METHODS.inputFilterKeyPressListener = function (event) {
			let input = event.currentTarget;

			let filterID = input.getAttribute("id-filter")
			let filter = STATE.filters[filterID]

			if (!filter) {
				error("El filtro no existe");
			}

			if (filter.autocomplete) return;

			if (event.keyCode == 13 && input.value != "") {
				METHODS.setFilter(filter.field, input.value, filter.tag, filter.alias);
				input.value = "";
			}
		}

		METHODS.orderConfirmClickListener = function () {
			if (!STATE.orderAction) return;

			const input = STATE.orderAction.querySelector("input");
			if (!input) return;

			const type = STATE.orderAction.querySelector(".order-type.active");
			if (!type) return;

			const order = METHODS.searchOrderByField(input.getAttribute("field"));
			if (!order) return;

			if (type.classList.contains("asc")) {
				METHODS.setOrder(order.field, "ASC", true, order.alias, "ASCENDENTE");
			} else {
				METHODS.setOrder(order.field, "DESC", true, order.alias, "DESCENDENTE");
			}
		}

		METHODS.orderTypeConfirmClickListener = function (event) {
			if (!STATE.orderAction) return;

			const activeType = STATE.orderAction.querySelector(".order-type.active");
			if (activeType) {
				activeType.classList.remove("active");
			}

			const button = event.currentTarget;
			button.classList.add("active");
		}

		METHODS.addCustomFilterListener = function (element) {
			if (element instanceof HTMLInputElement) {
				element.addEventListener("input", METHODS.inputFilterInputListener)
				element.addEventListener("keypress", METHODS.inputFilterKeyPressListener)
			}

			if (element instanceof HTMLSelectElement) {
				element.addEventListener("change", METHODS.inputFilterChangeListener)
			}

		}

		// ============ CONSTRUCTOR ============

		if (!element || !(element instanceof Element)) {
			error("El primer parámetro de Pagination no es un elemento válido");
		}

		STATE.object = element;
		deepMerge(CONFIG, config);

		STATE.dom.preHeader = Array.prototype.slice.call(STATE.object.children)

		METHODS.createDOM(() => {
			METHODS.addListeners();

			if (CONFIG.search) {
				METHODS.addSearchAction();
			}

			METHODS.setInitFiltersOrders();
			METHODS.selectPage(CONFIG.pagination.initPage);

			STATE.object.setAttribute("data-pagination", "true");
		})

		return METHODS;
	}

	return Pagination;
}());

export default Pagination;
